import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/** Menu sidebar BERSARANG yang bisa dipakai ulang.
 *
 * Lahir untuk brief owner 22 Sep 2026 bagian A ("Program Price Protection" jadi
 * parent → Program Support Aktivasi / Program Support Cashback → 4 halaman), dan
 * SENGAJA dibuat generik karena menu Claim MDR (bagian C, di bawah Piutang)
 * memakai pola yang sama. Jangan salin komponen ini ke layout lain — impor dari
 * sini; salinan kembar pernah berpisah jalan di repo ini (6 salinan pemilih
 * template, CLAUDE.md "Duplikasi helper melahirkan penyimpangan").
 *
 * Bentuk item:
 *   daun   : { to, end?, icon?, label, tid, modKey }
 *   parent : { tid, icon?, label, children: [item, ...] }   (boleh bersarang lagi)
 * `label` boleh fungsi `(konteks) => string` — dipakai untuk nama toko sendiri
 * dari Branding (white-label: "Support Aktivasi <nama toko>", keputusan A1).
 *
 * Perilaku (brief A):
 *   - klik parent = buka/tutup, chevron kanan berputar ke bawah saat terbuka;
 *   - animasi tinggi ringan (grid-rows 0fr↔1fr, tanpa mengukur DOM);
 *   - kalau halaman yang sedang dibuka ada di dalam cabang ini, SEMUA leluhurnya
 *     otomatis terbuka dan daunnya disorot seperti menu aktif lain;
 *   - parent tanpa anak yang boleh dilihat TIDAK dirender (lihat
 *     `saringNavMenurutIzin`) — parent kosong adalah tombol buntu.
 */

export function labelNav(label, konteks) {
  return typeof label === "function" ? label(konteks || {}) : label;
}

/** Sama dengan cara NavLink menandai aktif: `end` = persis, selain itu awalan
 *  segmen (bukan awalan huruf — "/finance/utang" tidak boleh menyala di
 *  "/finance/utang-lain"). */
export function ruteCocok(to, end, pathname) {
  if (!to) return false;
  const bersih = to.split("?")[0].replace(/\/+$/, "");
  if (pathname === bersih) return true;
  return !end && pathname.startsWith(`${bersih}/`);
}

export function mengandungRute(item, pathname) {
  if (item.children) return item.children.some((c) => mengandungRute(c, pathname));
  return ruteCocok(item.to, item.end, pathname);
}

/** Filter izin REKURSIF: daun lolos kalau `can(modKey, "view")` — kunci modul
 *  yang SAMA dengan `require_permission` endpoint halamannya (aturan #3) — dan
 *  parent hanya tersisa kalau masih punya anak. */
export function saringNavMenurutIzin(items, can) {
  return (items || [])
    .map((it) => {
      if (it.children) {
        const anak = saringNavMenurutIzin(it.children, can);
        return anak.length ? { ...it, children: anak } : null;
      }
      return can(it.modKey, "view") ? it : null;
    })
    .filter(Boolean);
}

function DaunNav({ item, konteks, tersembunyi }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to} end={item.end} data-testid={item.tid} tabIndex={tersembunyi ? -1 : undefined}
      className={({ isActive }) =>
        `flex items-start gap-2.5 px-3 py-2 rounded-lg text-[13px] leading-snug transition-colors ${
          isActive ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
        }`
      }
    >
      {Icon
        ? <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        : <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 shrink-0 mt-[7px]" />}
      {/* Tidak di-truncate: nama level 3 ("Support Aktivasi Dealer Partner")
          lebih panjang dari lebar sidebar setelah menjorok dua tingkat, dan nama
          yang terpotong membuat dua submenu tampak sama. Dibiarkan membungkus. */}
      <span className="min-w-0 break-words">{labelNav(item.label, konteks)}</span>
    </NavLink>
  );
}

export default function NavBersarang({ item, konteks, tingkat = 1, tersembunyi = false }) {
  const { pathname } = useLocation();
  const aktif = mengandungRute(item, pathname);
  const [buka, setBuka] = useState(aktif);
  // Berpindah ke halaman di dalam cabang ini (lewat tautan, notifikasi, atau
  // tombol Kembali) harus membuka cabangnya — keadaan tertutup yang dipilih
  // pengguna di halaman LAIN tidak boleh menyembunyikan menu yang sedang aktif.
  useEffect(() => { if (aktif) setBuka(true); }, [aktif, pathname]);

  // Daun level 3 duduk jauh di bawah sidebar Finance yang panjang — tanpa ini
  // menu yang aktif "terbuka" tapi di luar layar, padahal brief meminta state
  // aktif tetap terlihat jelas. Hanya menggeser gulungan <nav> sendiri (bukan
  // scrollIntoView, yang ikut menggulung halaman), dan hanya kalau daunnya
  // memang tidak terlihat. Ditunda sampai animasi buka selesai.
  const akarRef = useRef(null);
  useEffect(() => {
    if (!aktif || tingkat !== 1) return undefined;
    const t = setTimeout(() => {
      const daun = akarRef.current?.querySelector('[aria-current="page"]');
      const wadah = daun?.closest("nav");
      if (!daun || !wadah) return;
      const a = daun.getBoundingClientRect();
      const w = wadah.getBoundingClientRect();
      if (a.top < w.top || a.bottom > w.bottom) wadah.scrollTop += a.top - w.top - w.height / 3;
    }, 250);
    return () => clearTimeout(t);
  }, [aktif, pathname, tingkat]);

  const Icon = item.icon;
  const atas = tingkat === 1;
  const idPanel = `nav-sub-${item.tid}`;
  return (
    <div ref={akarRef}>
      <button
        type="button" onClick={() => setBuka((b) => !b)} data-testid={item.tid}
        aria-expanded={buka} aria-controls={idPanel} tabIndex={tersembunyi ? -1 : undefined}
        className={`w-full flex items-center gap-3 rounded-lg text-left transition-colors ${
          atas ? "px-3 py-2.5 text-sm" : "px-3 py-2 text-[13px]"
        } ${aktif ? "text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"}`}
      >
        {Icon && <Icon className={`${atas ? "w-4 h-4" : "w-3.5 h-3.5"} shrink-0`} />}
        <span className="flex-1 min-w-0 break-words leading-snug">{labelNav(item.label, konteks)}</span>
        <ChevronRight
          className={`w-4 h-4 shrink-0 text-white/50 transition-transform duration-200 ${buka ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      </button>
      {/* `inert` saat tertutup: tautan yang tidak terlihat tidak boleh bisa
          dicapai lewat Tab / pembaca layar. */}
      <div
        id={idPanel} data-testid={`${item.tid}-panel`} inert={!buka || tersembunyi ? true : undefined}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${buka ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="ml-4 pl-2 mt-0.5 border-l border-white/10 space-y-0.5">
            {item.children.map((anak) => (anak.children ? (
              <NavBersarang key={anak.tid} item={anak} konteks={konteks} tingkat={tingkat + 1}
                            tersembunyi={tersembunyi || !buka} />
            ) : (
              <DaunNav key={anak.to} item={anak} konteks={konteks} tersembunyi={tersembunyi || !buka} />
            )))}
          </div>
        </div>
      </div>
    </div>
  );
}
